import { getAccessToken, removeTokens } from '@repo/auth/core';
import { AuthStore as PackageAuthStore } from '@repo/auth/mobx';

import { AUTH_API_URL } from 'src/api/consts';
import { requireService } from 'src/packages/di';

import { authClient } from './auth-client';
import { KEYCLOAK_REALM_NAME } from './consts';

export type UserInfo = {
  name: string;
  preferred_username: string;
  email: string;
  family_name: string;
  given_name: string;
  email_verified: boolean;
  sub: string;
};

export const isUserInfo = (object: unknown): object is UserInfo =>
  typeof object === 'object' && !!object && 'sub' in object && 'preferred_username' in object;

export interface IAuthService {
  userInfo: UserInfo;
  logout(): void;
}

async function loadDashboardUserInfo(): Promise<UserInfo> {
  const accessToken = getAccessToken();

  if (!accessToken) {
    throw new Error('Access token is not presented');
  }

  const response = await fetch(`${AUTH_API_URL}/realms/${KEYCLOAK_REALM_NAME}/protocol/openid-connect/userinfo`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to load userinfo: ${response.status}`);
  }

  const data: unknown = await response.json();

  if (!isUserInfo(data)) {
    throw new Error('Invalid user object');
  }

  return data;
}

export const authStore = new PackageAuthStore<UserInfo>({
  authClient,
  loadUserInfo: loadDashboardUserInfo,
  onLogoutError: (error: unknown) => {
    console.error(error);

    try {
      requireService('notifications').showErrorMessageT('errors:failedToSignOut');
    } catch {
      // DI may be unavailable during early bootstrap
    }

    removeTokens();
    window.location.reload();
  },
});

export class AuthService implements IAuthService {
  constructor(private readonly store: PackageAuthStore<UserInfo>) {}

  get userInfo(): UserInfo {
    if (!this.store.userInfo) {
      throw new Error('User is not authenticated');
    }

    return this.store.userInfo;
  }

  logout = (): void => {
    void this.store.logout();
  };
}

/** @deprecated Use AuthService */
export const AuthStore = AuthService;
